import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, MapPin, LogOut, RefreshCcw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../lib/logger';
import { updateUserProfile } from '../../lib/userApi';
import { ALL_AVATAR_FILENAMES, getAvatarSrc } from '../../lib/avatarUtils';
import { useDeviceInfo } from '../../lib/deviceDetector';
import {
  getCalendarStatus,
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  pushToCalendar,
} from '../../lib/calendarApi';
import { CalendarStatus, ToastType } from '../../types/types';
import { LAST_CITY_KEY } from '../../lib/appConstants';
import CalendarConsentModal from './CalendarConsentModal';
import DecorativeCircles from './DecorativeCircles';

const GCAL_SYNC_KEY = 'ceen_gcal_last_sync';

const GOOGLE_G_SVG = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const GOOGLE_G_SMALL_SVG = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

function formatRelativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

interface ProfileViewProps {
  isVisible: boolean;
  onBack: () => void;
  showToast?: (message: string, type?: ToastType, duration?: number, dismissible?: boolean) => void;
  onRefreshCalendar?: () => void;
  onSwitchCity?: () => void;
}

const ProfileView: React.FC<ProfileViewProps> = ({
  isVisible,
  onBack,
  showToast,
  onRefreshCalendar,
  onSwitchCity,
}) => {
  const { backendUser, currentUser, logout, refreshUserData } = useAuth();
  const { isMobile } = useDeviceInfo();

  // ── Avatar ────────────────────────────────────────────────────────────────
  const avatarSize = isMobile ? 80 : 64;
  const avatarNavBtnSize = isMobile ? 32 : 28;
  const currentAvatarFilename = backendUser?.user_preferences?.avatar ?? '';
  const [avatarIndex, setAvatarIndex] = useState(() => {
    const idx = ALL_AVATAR_FILENAMES.indexOf(currentAvatarFilename);
    return idx >= 0 ? idx : 0;
  });
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const touchStartX = useRef<number | null>(null);

  // Resync avatarIndex if backendUser loads after initial render
  useEffect(() => {
    const filename = backendUser?.user_preferences?.avatar ?? '';
    const idx = ALL_AVATAR_FILENAMES.indexOf(filename);
    if (idx >= 0) setAvatarIndex(idx);
  }, [backendUser?.user_preferences?.avatar]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAvatarNav = async (direction: 'left' | 'right') => {
    if (isSavingAvatar || !backendUser) return;
    const total = ALL_AVATAR_FILENAMES.length;
    const newIndex =
      direction === 'left' ? (avatarIndex - 1 + total) % total : (avatarIndex + 1) % total;
    setAvatarIndex(newIndex);
    setIsSavingAvatar(true);
    try {
      const filename = ALL_AVATAR_FILENAMES[newIndex];
      await updateUserProfile(backendUser.id, {
        user_preferences: { ...backendUser.user_preferences, avatar: filename },
      });
      await refreshUserData();
    } catch (err) {
      logger.error('Failed to update avatar:', err);
    } finally {
      setIsSavingAvatar(false);
    }
  };

  // ── Name ──────────────────────────────────────────────────────────────────
  const [editingName, setEditingName] = useState(backendUser?.display_name || '');
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    setEditingName(backendUser?.display_name || '');
  }, [backendUser?.display_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveName = async () => {
    if (!backendUser) return;
    const trimmed = editingName.trim();
    if (trimmed === (backendUser.display_name || '')) return;
    setIsSavingName(true);
    try {
      await updateUserProfile(backendUser.id, { display_name: trimmed });
      await refreshUserData();
    } catch (err) {
      logger.error('Failed to update name:', err);
    } finally {
      setIsSavingName(false);
    }
  };

  // ── City ──────────────────────────────────────────────────────────────────
  const [currentCityName] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(LAST_CITY_KEY);
      return stored ? (JSON.parse(stored).name as string) : null;
    } catch {
      return null;
    }
  });

  // ── Logout ────────────────────────────────────────────────────────────────
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // ── Calendar ──────────────────────────────────────────────────────────────
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(null);
  const [showCalendarConsent, setShowCalendarConsent] = useState(false);
  const [isCalendarConnecting, setIsCalendarConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(() =>
    localStorage.getItem(GCAL_SYNC_KEY)
  );
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  useEffect(() => {
    if (currentUser) {
      getCalendarStatus()
        .then(setCalendarStatus)
        .catch(() => {});
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCalendarConnect = async () => {
    setIsCalendarConnecting(true);
    try {
      const status = await connectGoogleCalendar(currentUser?.email ?? '');
      setCalendarStatus(status);
      setShowCalendarConsent(false);
      await new Promise(res => setTimeout(res, 900));
      setIsSyncing(true);
      try {
        const result = await pushToCalendar();
        const now = new Date().toISOString();
        localStorage.setItem(GCAL_SYNC_KEY, now);
        setLastSyncedAt(now);
        const msg =
          result.synced === 0
            ? 'calendar connected'
            : `calendar connected · synced ${result.synced} event${result.synced === 1 ? '' : 's'}`;
        showToast?.(msg, 'success');
      } catch {
        showToast?.('calendar connected', 'success');
      } finally {
        setIsSyncing(false);
      }
    } catch (err) {
      logger.error('Calendar connect error:', err);
      showToast?.('calendar connection failed — try again', 'error');
    } finally {
      setIsCalendarConnecting(false);
    }
  };

  const handleCalendarDisconnect = async () => {
    setShowDisconnectConfirm(false);
    setIsDisconnecting(true);
    try {
      await disconnectGoogleCalendar();
      setCalendarStatus({ connected: false, google_email: null });
      localStorage.removeItem(GCAL_SYNC_KEY);
      setLastSyncedAt(null);
      showToast?.('calendar disconnected', 'info');
    } catch (err) {
      logger.error('Calendar disconnect error:', err);
      getCalendarStatus()
        .then(status => {
          setCalendarStatus(status);
          if (!status.connected) {
            localStorage.removeItem(GCAL_SYNC_KEY);
            setLastSyncedAt(null);
            showToast?.('calendar disconnected', 'info');
          } else {
            showToast?.('could not disconnect — try again', 'error');
          }
        })
        .catch(() => showToast?.('could not disconnect — try again', 'error'));
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCalendarSync = async () => {
    setIsSyncing(true);
    // Minimum visible duration so the step-3 animation is always felt
    const minDelay = new Promise<void>(res => setTimeout(res, 700));
    try {
      const [result] = await Promise.all([pushToCalendar(), minDelay]);
      const now = new Date().toISOString();
      localStorage.setItem(GCAL_SYNC_KEY, now);
      setLastSyncedAt(now);
      const msg =
        result.synced === 0
          ? 'nothing to sync'
          : `synced ${result.synced} event${result.synced === 1 ? '' : 's'}`;
      showToast?.(msg, 'success');
      onRefreshCalendar?.();
    } catch (err) {
      logger.error('Calendar sync error:', err);
      await minDelay;
      showToast?.('sync failed — try again', 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      onBack();
    } catch (err) {
      logger.error('Logout error:', err);
      showToast?.('logout failed — try again', 'error');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isCalendarConnected = !!calendarStatus?.connected;

  const isBundledAvatar = ALL_AVATAR_FILENAMES.includes(currentAvatarFilename);
  const displayAvatarFilename = isBundledAvatar
    ? (ALL_AVATAR_FILENAMES[avatarIndex] ?? '')
    : currentAvatarFilename;
  const displayAvatarSrc = displayAvatarFilename ? getAvatarSrc(displayAvatarFilename) : '';
  const displayName = backendUser?.display_name || currentUser?.email?.split('@')[0] || '';

  return (
    <div
      role="region"
      aria-label="profile"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        backgroundColor: 'var(--color-surface-subtle)',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        pointerEvents: isVisible ? 'auto' : 'none',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <DecorativeCircles />

      {/* Sticky back button */}
      <button
        onClick={onBack}
        aria-label="back"
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '16px 24px',
          background: 'var(--color-surface-subtle)',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--color-content-muted)',
          width: '100%',
          textAlign: 'left',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <ChevronLeft size={16} />
        back
      </button>

      {/* Scrollable content */}
      <div
        style={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          alignItems: 'center',
        }}
      >
        {/* Identity card — avatar/name/email/city + name-edit input, grouped as one unit */}
        <div
          style={{
            width: '100%',
            maxWidth: 320,
            padding: '20px',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--color-surface)',
            boxShadow: 'var(--shadow-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {/* Profile header — avatar on top, identity stacked below (full card width, no truncation) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '14px',
              width: '100%',
            }}
          >
            {/* Avatar with left/right nav */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <button
                onClick={() => handleAvatarNav('left')}
                disabled={isSavingAvatar}
                aria-label="previous avatar"
                style={{
                  width: avatarNavBtnSize,
                  height: avatarNavBtnSize,
                  borderRadius: '50%',
                  background: 'none',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSavingAvatar ? 'default' : 'pointer',
                  color: 'var(--color-content-muted)',
                  opacity: isSavingAvatar ? 0.4 : 1,
                  flexShrink: 0,
                  minHeight: 'unset',
                  minWidth: 'unset',
                }}
              >
                <ChevronLeft size={18} />
              </button>

              <div
                onTouchStart={e => {
                  touchStartX.current = e.touches[0].clientX;
                }}
                onTouchEnd={e => {
                  if (touchStartX.current === null) return;
                  const delta = e.changedTouches[0].clientX - touchStartX.current;
                  if (Math.abs(delta) > 40) handleAvatarNav(delta < 0 ? 'right' : 'left');
                  touchStartX.current = null;
                }}
                style={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-avatar-bg, #afdcfd)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  opacity: isSavingAvatar ? 0.7 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                {displayAvatarSrc ? (
                  <img
                    src={displayAvatarSrc}
                    alt="avatar"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: '30px',
                      color: 'var(--color-content-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    ?
                  </span>
                )}
              </div>

              <button
                onClick={() => handleAvatarNav('right')}
                disabled={isSavingAvatar}
                aria-label="next avatar"
                style={{
                  width: avatarNavBtnSize,
                  height: avatarNavBtnSize,
                  borderRadius: '50%',
                  background: 'none',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSavingAvatar ? 'default' : 'pointer',
                  color: 'var(--color-content-muted)',
                  opacity: isSavingAvatar ? 0.4 : 1,
                  flexShrink: 0,
                  minHeight: 'unset',
                  minWidth: 'unset',
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Identity: display name + email + city — full card width, centered, wraps instead of truncating */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              {displayName && (
                <div
                  style={{
                    fontSize: '17px',
                    fontWeight: 600,
                    color: 'var(--color-content)',
                    wordBreak: 'break-word',
                  }}
                >
                  {displayName}
                </div>
              )}
              {currentUser?.email && (
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-content-muted)',
                    wordBreak: 'break-word',
                  }}
                >
                  {currentUser.email}
                </div>
              )}
              {currentCityName && (
                <button
                  onClick={onSwitchCity}
                  aria-label="switch city"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '11px',
                    color: 'var(--color-content-muted)',
                    minHeight: 'unset',
                    minWidth: 'unset',
                  }}
                >
                  <MapPin size={11} />
                  {currentCityName}
                </button>
              )}
            </div>
          </div>

          {/* Name field — blur-to-save */}
          <div style={{ width: '100%' }}>
            <input
              value={editingName}
              onChange={e => setEditingName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={e => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              maxLength={50}
              disabled={isSavingName}
              placeholder="your display name"
              aria-label="display name"
              style={{
                width: '100%',
                padding: '6px 0',
                fontSize: '15px',
                fontWeight: 500,
                textAlign: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--color-border)',
                borderRadius: 0,
                color: 'var(--color-content)',
                outline: 'none',
                boxSizing: 'border-box',
                opacity: isSavingName ? 0.6 : 1,
              }}
            />
          </div>
        </div>

        {/* Calendar section label */}
        <div style={{ width: '100%', maxWidth: 320 }}>
          <span
            style={{
              fontSize: '12px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--color-content)',
              fontWeight: 700,
            }}
          >
            calendar
          </span>
        </div>

        {/* Calendar card — step progress + step content, grouped as one unit */}
        <div
          style={{
            width: '100%',
            maxWidth: 320,
            padding: '20px',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--color-surface)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {/* Calendar section — connection status + content. minHeight keeps the
                disconnect/logout buttons below from jumping as this swaps content. */}
          <div
            style={{
              width: '100%',
              minHeight: 48,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            {!isCalendarConnected &&
              (isCalendarConnecting ? (
                <span style={{ fontSize: '11px', color: 'var(--color-content-muted)' }}>
                  authorizing your account…
                </span>
              ) : (
                <button
                  onClick={() => setShowCalendarConsent(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '20px',
                    backgroundColor: '#F2F2F2',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#1F1F1F',
                    fontSize: '14px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8E8E8';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F2F2F2';
                  }}
                >
                  {GOOGLE_G_SVG}
                  Connect Google Calendar
                </button>
              ))}

            {isCalendarConnected && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {GOOGLE_G_SMALL_SVG}
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'var(--color-content)',
                      letterSpacing: '0.2px',
                    }}
                  >
                    google calendar connected
                  </div>
                  {/* Sync is manual only — no scheduled auto-sync exists today, so this
                      shows real state (last manual sync, or none yet) rather than claiming one. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: isSyncing
                          ? 'var(--color-content-muted)'
                          : 'var(--color-primary)',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: '10px', color: 'var(--color-content-muted)' }}>
                      {isSyncing
                        ? 'syncing…'
                        : lastSyncedAt
                          ? `last synced ${formatRelativeTime(lastSyncedAt)}`
                          : 'not synced yet'}
                    </span>
                  </div>
                </div>
                {/* Circular sync button — alignItems:center on parent centers it to full text block */}
                <button
                  onClick={isSyncing ? undefined : handleCalendarSync}
                  disabled={isSyncing}
                  aria-label="sync calendar now"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-surface-subtle)',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isSyncing ? 'default' : 'pointer',
                    color: isSyncing ? 'var(--color-content-muted)' : 'var(--color-primary)',
                    boxShadow: 'var(--shadow-sm)',
                    minHeight: 'unset',
                    minWidth: 'unset',
                    flexShrink: 0,
                    transition: 'color 0.15s ease',
                  }}
                >
                  <RefreshCcw size={16} style={{ opacity: isSyncing ? 0.5 : 1 }} />
                </button>
              </div>
            )}

            {/* Disconnect — available any time we're connected */}
            {isCalendarConnected && (
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 'var(--radius-pill)',
                  backgroundColor: 'var(--color-surface-subtle)',
                  border: '1px solid var(--color-border)',
                  boxShadow: 'var(--shadow-sm)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--color-error)',
                  minHeight: 'unset',
                  marginTop: '10px',
                }}
              >
                disconnect
              </button>
            )}
          </div>
        </div>

        {/* Logout — secondary danger: neutral glass + red text */}
        <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          aria-label="log out"
          aria-busy={isLoggingOut}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            maxWidth: 320,
            padding: '12px',
            borderRadius: 'var(--radius-pill)',
            backgroundColor: 'var(--color-surface-subtle)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-sm)',
            cursor: isLoggingOut ? 'default' : 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            color: isLoggingOut ? 'var(--color-content-muted)' : 'var(--color-error)',
          }}
        >
          <LogOut size={15} style={{ opacity: isLoggingOut ? 0.5 : 1 }} />
          {isLoggingOut ? 'logging out…' : 'log out'}
        </button>

        {/* Bottom padding for scroll comfort */}
        <div style={{ height: 24 }} />
      </div>

      {/* Disconnect confirmation modal — portal so z-index exceeds CityPicker */}
      {showDisconnectConfirm &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="disconnect-gcal-title"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--color-scrim)',
              padding: '24px',
            }}
            onClick={e => {
              if (e.target === e.currentTarget) setShowDisconnectConfirm(false);
            }}
          >
            <div
              style={{
                background: 'var(--color-surface)',
                borderRadius: '16px',
                padding: '24px',
                width: '100%',
                maxWidth: '340px',
                boxShadow: 'var(--shadow-lg)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div
                id="disconnect-gcal-title"
                style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-content)' }}
              >
                disconnect google calendar?
              </div>
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--color-content-muted)',
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                ceen will stop reading your events. events you&apos;ve already added to your
                calendar stay there.
              </p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--color-content)',
                  }}
                >
                  keep connected
                </button>
                <button
                  onClick={handleCalendarDisconnect}
                  disabled={isDisconnecting}
                  aria-busy={isDisconnecting}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '12px',
                    border: 'none',
                    background: isDisconnecting
                      ? 'var(--color-error-subtle)'
                      : 'var(--color-error)',
                    cursor: isDisconnecting ? 'default' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: isDisconnecting ? 'var(--color-error)' : 'white',
                  }}
                >
                  {isDisconnecting ? 'disconnecting…' : 'disconnect'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <CalendarConsentModal
        visible={showCalendarConsent}
        onDismiss={() => setShowCalendarConsent(false)}
        onConnect={handleCalendarConnect}
        isConnecting={isCalendarConnecting}
      />
    </div>
  );
};

export default ProfileView;
